import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import EnquiryStageHistory from "../../components/EnquiryStageHistory/EnquiryStageHistory";
import ProductEditModal from "../Enquiries/EnquiryStageEditModal";
import WebSocketService from "../../components/WebSockets/WebSocketService";
import EnquiryBOMModal from "../../components/EnquiryStageHistory/EnquiryBOMModal";
import MinutesOfMeetingManager from "../../components/EnquiryStageHistory/MinutesOfMeetingManager";
import "./TeamEnquiry.scss";

// The card list now comes from a lightweight, paginated endpoint
// (`/enquiry-stage-history/employee/details`) that returns a flat array of
// summaries — one per card — instead of the old array-of-stages-with-nested-
// histories shape the `/enquiry-stage-history/team` endpoint used to return.
// <EnquiryStageHistory /> still expects that older grouped shape, so this
// regroups the flat list by stage without touching that component. Full
// per-card detail is still fetched lazily on expand (inside KanbanCard, via
// the team detail endpoint).
const mapLightweightRecordToHistoryItem = (record) => ({
  id: record.id,
  enquiryId: record.enquiryId,
  leadName: record.leadName,
  clientName: record.clientName,
  assignedTo: record.assignedTo,
  priority: record.priority,
  dueAt: record.dueAt,
  createdAt: record.createdAt,
  updatedAt: record.updatedAt,
  endedAt: record.endedAt,
  notes: record.notes,
  remarks: "",
  checkListHistories: [],
  // These come pre-computed from the lightweight endpoint and must be
  // passed through as-is — EnquiryStageHistory's transform only trusts its
  // own checklist counting when these are undefined, and since
  // checkListHistories above is always [], omitting these here made every
  // card fall back to counting placeholder tasks (all "incomplete"),
  // blocking stage moves even when the API says the checklist is done.
  completedTasks: record.completedTasks,
  totalTasks: record.totalTasks,
  productEnquiryHistories: (record.productHistories || []).map((p) => ({
    id: p.id,
    productId: p.id,
    productName: p.productName,
    quantity: p.quantity,
  })),
  totalQuotationAmount: 0,
});

const buildEmptyStageGroup = (stageId, record, stageTemplateMap) => {
  const template = stageTemplateMap[stageId] || {};
  return {
    enquiryStageId: stageId,
    stageName: template.stageName || "",
    stageType: template.stageType,
    priority: record.priority,
    customPricing: false,
    totalQuotationAmount: 0,
    histories: [],
  };
};

// Groups a flat lightweight list into the per-stage shape EnquiryStageHistory
// expects. Used for the first page of results.
const groupLightweightStageHistory = (list, stageTemplates = []) => {
  const stageTemplateMap = {};
  (stageTemplates || []).forEach((stage) => {
    stageTemplateMap[stage.enquiryStageId] = stage;
  });

  const groupsByStageId = {};

  (list || []).forEach((record) => {
    const stageId = record.stageId;
    if (!groupsByStageId[stageId]) {
      groupsByStageId[stageId] = buildEmptyStageGroup(
        stageId,
        record,
        stageTemplateMap,
      );
    }
    groupsByStageId[stageId].histories.push(
      mapLightweightRecordToHistoryItem(record),
    );
  });

  return Object.values(groupsByStageId);
};

// Merges another page of lightweight records into an already-grouped
// stageHistory list — used for "load more" so earlier pages/columns aren't
// touched or duplicated.
const appendLightweightStageHistory = (
  existingGroups,
  newRecords,
  stageTemplates = [],
) => {
  const stageTemplateMap = {};
  (stageTemplates || []).forEach((stage) => {
    stageTemplateMap[stage.enquiryStageId] = stage;
  });

  const groupsByStageId = {};
  const seenIds = new Set();
  (existingGroups || []).forEach((group) => {
    groupsByStageId[group.enquiryStageId] = {
      ...group,
      histories: [...group.histories],
    };
    group.histories.forEach((h) => seenIds.add(h.id));
  });

  (newRecords || []).forEach((record) => {
    if (seenIds.has(record.id)) return; // avoid dupes if a page overlaps
    seenIds.add(record.id);
    const stageId = record.stageId;
    if (!groupsByStageId[stageId]) {
      groupsByStageId[stageId] = buildEmptyStageGroup(
        stageId,
        record,
        stageTemplateMap,
      );
    }
    groupsByStageId[stageId].histories.push(
      mapLightweightRecordToHistoryItem(record),
    );
  });

  return Object.values(groupsByStageId);
};

// Merges the response from the single-card detail endpoint
// (`/enquiry-stage-history/manager/employee/{enquiryStageHistoryId}`)
// into the already-grouped stageHistory list, replacing just that one card
// in place. Used after completing a checklist item so only that card's data
// (and the checklist counts derived from it) gets refreshed, instead of
// refetching every column like fetchStageHistory does.
// Returns null if the card couldn't be located in its expected stage group
// (e.g. it moved stages since the last full fetch), signalling the caller
// to fall back to a full refresh instead of silently dropping the update.
const mergeDetailedHistoryIntoStageHistory = (
  existingGroups,
  detail,
  targetHistoryId,
) => {
  const histories = detail?.histories || [];
  // Match the specific card we asked about by id rather than assuming it's
  // first in the array — the backend may return more than one history
  // entry (e.g. an enquiry's past stage visits) alongside the current one.
  const updatedHistory =
    histories.find((h) => String(h.id) === String(targetHistoryId)) ||
    histories[0];
  if (!updatedHistory) return null;

  const mappedItem = {
    id: updatedHistory.id,
    enquiryId: updatedHistory.enquiryId,
    leadName: updatedHistory.leadName,
    leadCode: updatedHistory.leadCode,
    clientName: updatedHistory.clientName,
    clientCode: updatedHistory.clientCode,
    assignedTo: updatedHistory.assignedTo,
    assignedEmployeeId: updatedHistory.assignedEmployeeId,
    startedAt: updatedHistory.startedAt,
    endedAt: updatedHistory.endedAt,
    dueAt: updatedHistory.dueAt,
    notes: updatedHistory.notes,
    remarks: updatedHistory.remarks,
    enquiryStatus: updatedHistory.enquiryStatus,
    enquiryStageName: updatedHistory.enquiryStageName,
    productEnquiryHistories: (updatedHistory.productEnquiryHistories || []).map(
      (p) => ({
        id: p.id,
        productId: p.productId,
        productName: p.productName,
        skuCode: p.skuCode,
        quantity: p.quantity,
        price: p.price,
        fulfillmentStatus: p.fulfillmentStatus,
        specificationHistories: p.specificationHistories || [],
      }),
    ),
    totalQuotationAmount: updatedHistory.totalQuotationAmount ?? 0,
  };

  // The detail endpoint has occasionally been observed returning
  // checkListHistories: [] for a card that already has items ticked in
  // state (a backend timing quirk, not an actual "no checklist" card).
  // Trusting an empty array blindly would wipe out real progress the user
  // just completed, so only replace checkListHistories/completedTasks/
  // totalTasks when the fresh payload actually has items — otherwise keep
  // whatever's already in state for that card.
  const freshChecklists = updatedHistory.checkListHistories || [];

  // Use the history record's own enquiryStageId (not the envelope's
  // top-level one) to find the owning column — it's the field that's
  // actually guaranteed to correspond to this specific card. IDs coming
  // back from different endpoints aren't always the same JS type (string
  // vs number), so compare as strings rather than with `!==`.
  const ownerStageId = updatedHistory.enquiryStageId ?? detail?.enquiryStageId;

  let found = false;
  const nextGroups = existingGroups.map((group) => {
    if (String(group.enquiryStageId) !== String(ownerStageId)) return group;
    const nextHistories = group.histories.map((h) => {
      if (String(h.id) !== String(mappedItem.id)) return h;
      found = true;
      const checkListHistories =
        freshChecklists.length > 0
          ? freshChecklists
          : h.checkListHistories || [];
      return {
        ...h,
        ...mappedItem,
        checkListHistories,
        completedTasks: checkListHistories.filter((cl) => cl.completed).length,
        totalTasks: checkListHistories.length,
      };
    });
    if (!found) return group;
    return {
      ...group,
      stageName: detail?.stageName || group.stageName,
      stageType: detail?.stageType || group.stageType,
      customPricing:
        detail?.customPricing !== undefined
          ? detail.customPricing
          : group.customPricing,
      histories: nextHistories,
    };
  });

  return found ? nextGroups : null;
};

const STAGE_HISTORY_PAGE_SIZE = 10;

const TeamEnquiry = () => {
  const { user } = useAuth();

  const [stageHistory, setStageHistory] = useState([]);
  // Lightweight per-stage totals (accurate totalEnquiries/totalQuotationAmount
  // across ALL of a stage's enquiries, not just the loaded page) from
  // `/enquiry-stage-history/all/summary`. Fetched first so kanban column
  // headers can populate immediately; the slower paginated detail fetch
  // below still drives the actual card list per column.
  const [stageSummary, setStageSummary] = useState([]);
  const [enquiryStages, setEnquiryStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStages, setLoadingStages] = useState(false);
  const [error, setError] = useState("");
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  // Pagination is per-stage/per-column, since the backend requires a
  // `stageId` and paginates that stage's records independently. The API
  // itself takes `page` (1-based: 1, 2, 3...) and `size` instead of
  // fromRecord/toRecord. Keyed by enquiryStageId:
  //   paginationByStage[stageId] = { nextPage, hasMore }
  //   loadingStageIds = Set of stageIds currently fetching their next page
  const [paginationByStage, setPaginationByStage] = useState({});
  const [loadingStageIds, setLoadingStageIds] = useState(() => new Set());
  const loadingStageIdsRef = useRef(new Set());
  // Mirrors `enquiryStages` for use inside fetchStageHistory without adding
  // it as a useCallback dependency.
  const enquiryStagesRef = useRef([]);

  const [productEditModal, setProductEditModal] = useState({
    isOpen: false,
    productHistory: null,
    productHistoryId: null,
  });

  const [showBOMModal, setShowBOMModal] = useState(false);
  const [bomEnquiryData, setBomEnquiryData] = useState(null);

  const [momModal, setMomModal] = useState({
    isOpen: false,
    enquiryId: null,
    enquiryStageHistoryId: null,
  });

  const getApiEndpoint = (endpoint) =>
    `${process.env.REACT_APP_SALES_MANAGER}${endpoint}`;

  /* ---------------- FETCH STAGES ---------------- */

  const fetchEnquiryStages = useCallback(async () => {
    try {
      setLoadingStages(true);
      const res = await axiosInstance.get(getApiEndpoint("/enquiry-stages"));
      const stages = res.data?.data || [];
      setEnquiryStages(stages);
      enquiryStagesRef.current = stages;
    } catch (err) {
      setError("Failed to fetch enquiry stages");
    } finally {
      setLoadingStages(false);
    }
  }, []);

  /* ---------------- FETCH STAGE HISTORY ---------------- */

  // Fetches the lightweight per-stage summary — total enquiry count and
  // total quotation amount for every stage in one call, independent of
  // pagination — and uses it to populate each kanban column's header stats
  // right away. This is triggered first (see fetchStageHistory below), well
  // ahead of the slower per-stage paginated detail fetch that fills in the
  // actual cards.
  const fetchStageSummary = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        getApiEndpoint("/enquiry-stage-history/all/summary"),
      );
      setStageSummary(response.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch enquiry stage summary:", err);
    }
  }, []);

  // Fetches the first page for every stage/column in parallel and rebuilds
  // stageHistory from scratch. Needs the stage list first (each stage's
  // `enquiryStageId` is a required query param), so this reads
  // enquiryStagesRef.current rather than taking stages as an argument —
  // callers should make sure stages are loaded before calling this (see the
  // mount effect below).
  const fetchStageHistory = useCallback(async () => {
    const stages = enquiryStagesRef.current;
    if (!stages || stages.length === 0) return;

    // Trigger the summary fetch first — it doesn't depend on the paginated
    // detail below and lets kanban column headers populate immediately.
    fetchStageSummary();

    try {
      setLoading(true);

      const results = await Promise.all(
        stages.map((stage) =>
          axiosInstance
            .get(getApiEndpoint("/enquiry-stage-history/employee/details"), {
              params: {
                stageId: stage.enquiryStageId,
                page: 0,
                size: STAGE_HISTORY_PAGE_SIZE,
              },
            })
            .then((response) => ({
              stageId: stage.enquiryStageId,
              records: response.data?.data || [],
            }))
            .catch((err) => {
              console.error(
                `Failed to fetch stage history for stage ${stage.enquiryStageId}:`,
                err,
              );
              return { stageId: stage.enquiryStageId, records: [] };
            }),
        ),
      );

      const allRecords = results.flatMap((result) => result.records);
      setStageHistory(groupLightweightStageHistory(allRecords, stages));

      const nextPaginationByStage = {};
      results.forEach(({ stageId, records }) => {
        nextPaginationByStage[stageId] = {
          // Initial load used page 0, so the next page to fetch on scroll is 1.
          nextPage: 1,
          // A full page means there's likely more behind it; a short page
          // means this stage has nothing further to give us.
          hasMore: records.length === STAGE_HISTORY_PAGE_SIZE,
        };
      });
      setPaginationByStage(nextPaginationByStage);
    } catch (err) {
      setError("Failed to fetch team enquiry history");
    } finally {
      setLoading(false);
    }
  }, [fetchStageSummary]);

  // Loads the next page for a single column and merges it into the
  // already-grouped stageHistory (see appendLightweightStageHistory above).
  // This is what a column's own scroll container (inside
  // <EnquiryStageHistory />) calls as the user nears the bottom of that
  // specific column.
  const fetchMoreStageHistoryForStage = useCallback(
    async (stageId) => {
      const stagePagination = paginationByStage[stageId];
      if (!stagePagination?.hasMore) return;
      if (loadingStageIdsRef.current.has(stageId)) return; // already in flight

      loadingStageIdsRef.current.add(stageId);
      setLoadingStageIds(new Set(loadingStageIdsRef.current));

      try {
        const page = stagePagination.nextPage;

        const response = await axiosInstance.get(
          getApiEndpoint("/enquiry-stage-history/employee/details"),
          {
            params: { stageId, page, size: STAGE_HISTORY_PAGE_SIZE },
          },
        );
        const records = response.data?.data || [];

        setStageHistory((prev) =>
          appendLightweightStageHistory(
            prev,
            records,
            enquiryStagesRef.current,
          ),
        );

        setPaginationByStage((prev) => ({
          ...prev,
          [stageId]: {
            nextPage: page + 1,
            hasMore: records.length === STAGE_HISTORY_PAGE_SIZE,
          },
        }));
      } catch (err) {
        console.error(
          `Failed to fetch more stage history for stage ${stageId}:`,
          err,
        );
      } finally {
        loadingStageIdsRef.current.delete(stageId);
        setLoadingStageIds(new Set(loadingStageIdsRef.current));
      }
    },
    [paginationByStage],
  );

  /* ---------------- CHECKLIST ---------------- */

  // Refetches just the one card that changed, via the single-card detail
  // endpoint, and splices the result back into stageHistory in place. This
  // is what keeps completing a checklist item from reloading the whole
  // board — only this GET fires, not one per stage/column.
  const refreshSingleStageHistory = useCallback(
    async (enquiryStageHistoryId) => {
      try {
        const response = await axiosInstance.get(
          getApiEndpoint(
            `/enquiry-stage-history/manager/employee/${enquiryStageHistoryId}`,
          ),
        );
        const detail = response.data?.data;
        let mergedOk = true;

        setStageHistory((prev) => {
          const merged = mergeDetailedHistoryIntoStageHistory(
            prev,
            detail,
            enquiryStageHistoryId,
          );
          if (!merged) {
            mergedOk = false;
            return prev;
          }
          return merged;
        });

        if (!mergedOk) {
          // Card wasn't where we expected (e.g. moved stage in the
          // meantime) — fall back to a full refresh rather than leaving
          // stale data in place.
          console.warn(
            `Could not locate enquiry stage history ${enquiryStageHistoryId} in current board state; falling back to full refresh.`,
          );
          fetchStageHistory();
          return;
        }

        // Keep the per-column header counts (total enquiries/quotation
        // amount) in sync too — this alone is a lightweight call, not the
        // full per-stage detail fetch.
        fetchStageSummary();
      } catch (err) {
        console.error(
          `Failed to refresh enquiry stage history ${enquiryStageHistoryId}:`,
          err,
        );
        // If the targeted GET itself failed (network error, endpoint
        // issue, etc.) don't leave the UI silently stale — fall back to a
        // full refresh so the completed checklist item still shows up.
        fetchStageHistory();
      }
    },
    [fetchStageHistory, fetchStageSummary],
  );

  const completeChecklistItem = async (
    checklistHistoryId,
    remarks = "",
    enquiryStageHistoryId,
  ) => {
    try {
      await axiosInstance.put(
        getApiEndpoint(`/enquiry/checklist-history/${checklistHistoryId}`),
        { completed: true, remarks },
      );
      toast.success("Checklist completed");
      if (enquiryStageHistoryId) {
        // Targeted refresh: only the affected card's endpoint is called.
        await refreshSingleStageHistory(enquiryStageHistoryId);
      } else {
        // No card id was available (shouldn't normally happen) — fall back
        // to a full refresh so the UI doesn't go stale.
        fetchStageHistory();
      }
      return true;
    } catch {
      return false;
    }
  };

  /* ---------------- STAGE UPDATE ---------------- */

  const updateEnquiryStage = async (historyId, nextStageId) => {
    try {
      await axiosInstance.put(getApiEndpoint("/enquiry-stage-history"), {
        enquiryStageHistoryId: historyId,
        nextEnquiryStageId: nextStageId,
      });
      toast.success("Enquiry moved to next stage");
      fetchStageHistory();
      return true;
    } catch {
      return false;
    }
  };

  /* ---------------- PRODUCT EDIT ---------------- */

  const openProductEditModal = (productHistory, productHistoryId) => {
    setProductEditModal({
      isOpen: true,
      productHistory,
      productHistoryId,
    });
  };

  const closeProductEditModal = () => {
    setProductEditModal({
      isOpen: false,
      productHistory: null,
      productHistoryId: null,
    });
  };

  const updateProductHistory = async (formData) => {
    try {
      await axiosInstance.put(
        getApiEndpoint(
          `/enquiry/product-history/${productEditModal.productHistoryId}`,
        ),
        formData,
      );
      toast.success("Product updated");
      closeProductEditModal();
      fetchStageHistory();
      return true;
    } catch {
      return false;
    }
  };

  const handleOpenMOM = (stageHistoryItem) => {
    setMomModal({
      isOpen: true,
      enquiryId: stageHistoryItem.enquiryId,
      enquiryStageHistoryId: stageHistoryItem.id,
    });
  };

  /* ---------------- BOM ---------------- */

  const openBOMModal = (item) => {
    setBomEnquiryData({
      ...item,
      productOrderHistoryId: item.id,
    });
    setShowBOMModal(true);
  };

  const closeBOMModal = () => {
    setShowBOMModal(false);
    setBomEnquiryData(null);
  };

  /* ---------------- WEBSOCKET ---------------- */

  useEffect(() => {
    fetchStageSummary();

    (async () => {
      await fetchEnquiryStages();
      fetchStageHistory();
    })();

    const tenantId = sessionStorage.getItem("tenantId");
    if (!tenantId || !user?.empId) return;

    WebSocketService.connect({
      tenantId,
      empId: user.empId,
    });

    const interval = setInterval(() => {
      setIsWebSocketConnected(WebSocketService.isConnected());
    }, 2000);

    const debounceTimerRef = { current: null };

    const handleTeamEnquiryUpdate = (data) => {
      console.log("📨 Received team enquiry update:", data);
      toast.info("Team enquiry updated");

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchStageHistory();
      }, 1000);
    };

    WebSocketService.subscribeToManagerEnquiryUpdates(
      tenantId,
      user.empId,
      handleTeamEnquiryUpdate,
    );

    return () => {
      clearInterval(interval);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      WebSocketService.unsubscribeFromTopic("manager-enquiry");
    };
  }, [user?.empId, fetchStageHistory, fetchStageSummary, fetchEnquiryStages]);

  /* ---------------- RENDER ---------------- */

  return (
    <>
      <div className="enquiries-page team-enquiries">
        {error && (
          <div className="error-message">
            {error}
            <ButtonWithIcon
              icon="mdi:close"
              text=""
              onClick={() => setError("")}
            />
          </div>
        )}

        {(loading || loadingStages) && <LoadingScreen />}

        <EnquiryStageHistory
          data={stageHistory}
          stageSummary={stageSummary}
          loading={loading}
          enquiryStages={enquiryStages}
          onRefresh={fetchStageHistory}
          onStageUpdate={updateEnquiryStage}
          onProductEdit={openProductEditModal}
          onChecklistComplete={completeChecklistItem}
          onChecklistQuickComplete={completeChecklistItem}
          currentUserId={user?.id}
          isTeamView={true}
          isWebSocketConnected={isWebSocketConnected}
          onOpenBOM={openBOMModal}
          onOpenMOM={handleOpenMOM}
          getApiEndpoint={getApiEndpoint}
          axiosInstance={axiosInstance}
          // Pagination is per-column (the backend requires a stageId and
          // paginates that stage's records independently), so there's no
          // single "end of list" for the whole board. Each column calls
          // onLoadMoreForStage(stageId) as the user nears the bottom of
          // that specific column's scroll container.
          paginationByStage={paginationByStage}
          loadingStageIds={loadingStageIds}
          onLoadMoreForStage={fetchMoreStageHistoryForStage}
        />

        <ProductEditModal
          isOpen={productEditModal.isOpen}
          onClose={closeProductEditModal}
          productHistory={productEditModal.productHistory}
          onSave={updateProductHistory}
        />

        <EnquiryBOMModal
          isOpen={showBOMModal}
          onClose={closeBOMModal}
          enquiryData={bomEnquiryData}
          axiosInstance={axiosInstance}
          onSuccess={() => {
            toast.success("BOM updated");
            fetchStageHistory();
          }}
        />
      </div>

      {/* MOM Modal with overlay */}
      {momModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <MinutesOfMeetingManager
              enquiryId={momModal.enquiryId}
              enquiryStageHistoryId={momModal.enquiryStageHistoryId}
              axiosInstance={axiosInstance}
              getApiEndpoint={getApiEndpoint}
              onClose={() =>
                setMomModal({
                  isOpen: false,
                  enquiryId: null,
                  enquiryStageHistoryId: null,
                })
              }
            />
          </div>
        </div>
      )}
    </>
  );
};

export default TeamEnquiry;
